import type { IButton } from "@Components/Button";
import { Button } from "@Components/Button";
import type { IComponentAjax, IComponentAjaxState } from "@Components/ComponentAjax";
import { ComponentAjax } from "@Components/ComponentAjax";
import { ErrorContainer } from "@Components/ErrorContainer";
import { Loading } from "@Components/Loading";
import { AjaxMethod } from "@Components/Modules/Ajax";
import type { IErrors } from "@Components/Modules/ErrorHandler";
import { ErrorType, clearAllErrors, setValidate } from "@Components/Modules/ErrorHandler";
import { requestTrack } from "@Components/Modules/Helpers";
import { isMailValid, isNullOrEmpty } from "@Components/Modules/Validations";
import type { ITextbox } from "@Components/Textbox";
import type { IValidator } from "@Components/Validator";

import type { INavigationModalActions } from "../common/NavigationModal";
import type { IUserCommunicationTemplate } from "../common/UserCommunicationTemplate";
import type { IInputTracker } from "../telemetry/InputTracker";
import { TextboxTracker } from "../telemetry/InputTracker";
import type { IEmailConfirmationFields } from "./EmailConfirmation";

import * as cssNavModal from "../common/NavigationModal.module.css";
import * as css from "./EmailEntryForm.module.css";

export interface IEmailEntryForm
    extends INavigationModalActions<IEmailConfirmationFields>, IComponentAjax<IUserCommunicationTemplate> {
    title: string;
    emailLabel: string;
    emailTextbox: IInputTracker<ITextbox>;
    button: IButton;
    emailTextboxRequiredError: IValidator;
    emailTextboxInvalidError: IValidator;
    emailTextboxTakenError: null;
    emailTextboxDefaultError: null;
    ajaxVisualAction: string;
}

interface EmailEntryFormState extends IComponentAjaxState {
    emailTextboxValue: string;
    disabledButton: boolean;
    inputPassword: string;
    userCommunicationTemplate?: IUserCommunicationTemplate;
}

export class EmailEntryForm extends ComponentAjax<
    IUserCommunicationTemplate,
    IEmailEntryForm,
    EmailEntryFormState
> {
    private get renderTextBox() {
        const emailValueTextbox: IInputTracker<ITextbox> = {
            ...this.props.emailTextbox,
            focusTelemetryCustom: { eventName: "usprfl-emailEdit", actionType: "Undefined" },
            properties: {
                ...this.props.emailTextbox.properties,
                maxLength: 200,
                onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
                    this.onChangeAmount(event.target.value),
                value: this.state.emailTextboxValue,
            },
        };

        return (
            <div className={css.inputNewValue}>
                <label className={cssNavModal.standardModalLabel}>{this.props.emailLabel}</label>
                <TextboxTracker {...emailValueTextbox} ref="emailValue-textbox" />
                <ErrorContainer {...this.errors.emailTextboxRequiredError.container} />
                <ErrorContainer {...this.errors.emailTextboxInvalidError.container} />
                <ErrorContainer {...this.errors.emailTextboxTakenError.container} />
                <ErrorContainer {...this.errors.emailTextboxDefaultError.container} />
            </div>
        );
    }

    public static readonly defaultProps: IEmailEntryForm = {
        ...ComponentAjax.defaultProps,
        title: null,
        emailLabel: null,
        emailTextbox: null,
        button: null,
        emailTextboxRequiredError: null,
        emailTextboxInvalidError: null,
        emailTextboxTakenError: null,
        emailTextboxDefaultError: null,
        ajaxVisualAction: null,
    };

    public errors: IErrors = {
        default: { ...this.defaultErrors().default, type: ErrorType.Validator },
        emailTextboxRequiredError: {
            name: "email-required",
            type: ErrorType.Validator,
            targetRef: "emailValue-textbox",
            container: { ref: "email-required-container" },
            onValidate: () => {
                if (isNullOrEmpty(this.state.emailTextboxValue) === false) {
                    return true;
                }
                return false;
            },
        },
        emailTextboxInvalidError: {
            name: "email-invalid",
            type: ErrorType.Validator,
            targetRef: "emailValue-textbox",
            container: { ref: "email-invalid-container" },
            onValidate: () => {
                if (isNullOrEmpty(this.state.emailTextboxValue) === true) {
                    return true;
                }
                return isMailValid(this.state.emailTextboxValue);
            },
        },
        emailTextboxTakenError: {
            name: "email-taken",
            type: ErrorType.Validator,
            targetRef: "emailValue-textbox",
            container: { ref: "email-taken-container" },
            onValidate: () => true,
        },
        emailTextboxDefaultError: {
            name: "email-default-error",
            type: ErrorType.Validator,
            targetRef: "emailValue-textbox",
            container: { ref: "email-default-error-container" },
            onValidate: () => true,
        },
    };

    constructor(props: IEmailEntryForm) {
        super({ ...props, ajaxMethod: AjaxMethod.Post });

        const fields = this.props.fieldValues;

        if (fields) {
            this.state = {
                isLoading: false,
                emailTextboxValue: fields.emailValue,
                disabledButton: fields.emailValue == null,
                inputPassword: fields.passwordValue,
            };
        } else {
            this.state = {
                isLoading: false,
                emailTextboxValue: null,
                disabledButton: false,
                inputPassword: fields.passwordValue,
            };
        }

        this.submitForm = this.submitForm.bind(this);
        this.onChangeAmount = this.onChangeAmount.bind(this);
    }

    public componentDidMount() {
        requestTrack(this.props.ajaxVisualAction);
    }

    public render() {
        return (
            <div {...this.rootDefaultAttributes(css.emailEntryFormComponent, cssNavModal.standardModalForm)}>
                <h1 className={cssNavModal.standardModalTitle}>{this.props.title}</h1>
                <div className={cssNavModal.standardModalFormBody}>{this.renderTextBox}</div>
                <div className={css.buttonContent}>
                    <Button
                        {...this.props.button}
                        disabled={
                            this.state.disabledButton ||
                            this.state.emailTextboxValue == null ||
                            this.state.emailTextboxValue == ""
                        }
                        onClick={this.submitForm}
                    />
                </div>
                {this.state.isLoading && <Loading isModal />}
            </div>
        );
    }

    protected ajaxFail() {
        this.setState(() => ({ isLoading: false }));
        return true;
    }

    protected ajaxSuccess(response: IUserCommunicationTemplate) {
        this.setState({ isLoading: false });

        if (response) {
            if (this.props.onSubmit) {
                this.props.saveValues.call(this, { userCommunicationTemplate: response });
                this.props.onSubmit.call(this);
            }
        }

        setValidate(this, this.errors.emailTextboxDefaultError);
    }

    private onChangeAmount(value: string) {
        clearAllErrors(this);

        this.setState({ emailTextboxValue: value, disabledButton: value == null || value == "" });
    }

    private submitForm() {
        const params = {
            newEmail: this.state.emailTextboxValue,
            password: this.state.inputPassword,
        };

        this.ajaxRequest(params, () => this.setState({ isLoading: true }));
    }
}
